<template>
  <!-- PACS 影像對照觀看 dialog（Vuetify 1.3，與既有 CaptureImageDialog 同風格） -->
  <v-dialog
    :value="show"
    max-width="1100"
    persistent
    content-class="pacs-dicom-viewer"
  >
    <v-card>
      <v-card-title class="pacs-viewer__title">
        <span>{{ $t('pacs.viewer_title') }}</span>
        <v-spacer />
        <v-btn icon @click="close">
          <v-icon>close</v-icon>
        </v-btn>
      </v-card-title>

      <v-divider />

      <v-card-text class="pacs-viewer__body">
        <!-- 左：影像群組清單 -->
        <div class="pacs-viewer__sidebar">
          <div class="pacs-viewer__section-label">{{ $t('pacs.studies') }}</div>

          <div v-if="loadingImageSets" class="pacs-viewer__hint">
            <v-progress-circular indeterminate size="20" color="primary" />
            {{ $t('pacs.querying') }}
          </div>

          <div v-else-if="imageSets.length === 0" class="pacs-viewer__hint">
            {{ $t('pacs.no_study') }}
          </div>

          <!--
            ⚠️ 標題 / 副標一律走 fallback：ImageSet 除 imageSetId 外全是選填。
            醫位 WADO 不回任何 metadata（無檢查日期 / modality / 描述），
            硬綁 studyDate 會讓整排清單長成「- · -」讓人以為壞掉。
          -->
          <v-list v-else dense class="pacs-viewer__study-list">
            <v-list-tile
              v-for="imageSet in imageSets"
              :key="imageSet.imageSetId"
              :class="{
                'pacs-viewer__study--active':
                  imageSet.imageSetId === activeImageSetId,
              }"
              @click="onSelectImageSet(imageSet)"
            >
              <v-list-tile-content>
                <v-list-tile-title>
                  {{ imageSetTitle(imageSet) }}
                </v-list-tile-title>
                <v-list-tile-sub-title>
                  {{ imageSetSubtitle(imageSet) }}
                </v-list-tile-sub-title>
              </v-list-tile-content>
            </v-list-tile>
          </v-list>
        </div>

        <!-- 右：dwv DICOM viewer -->
        <div class="pacs-viewer__main">
          <div v-if="retrieving" class="pacs-viewer__overlay">
            <v-progress-circular indeterminate size="40" color="primary" />
            <div>{{ $t('pacs.retrieving') }}</div>
          </div>

          <div v-if="errorMsg" class="pacs-viewer__error">{{ errorMsg }}</div>

          <!--
            部分失敗提示：33 張只拿到 30 張時，若不講，畫面看起來與「本來就只有
            30 張」完全一樣，醫師無從得知自己少看了什麼影像（見 ADR 0006）。
          -->
          <div v-if="failedCount > 0" class="pacs-viewer__warning">
            {{ $t('pacs.partial_failed', { failed: failedCount }) }}
          </div>

          <!-- dwv 掛載點：dwv 是純 JS viewer，由 mounted/方法手動操作其 App 生命週期 -->
          <div
            ref="dwvLayer"
            id="pacs-dwv-layer-group"
            class="pacs-viewer__canvas"
          ></div>

          <!-- 影像切換工具列 -->
          <div
            v-if="instanceCount > 1"
            class="pacs-viewer__pager"
          >
            <v-btn icon small :disabled="currentIndex <= 0" @click="prevImage">
              <v-icon>chevron_left</v-icon>
            </v-btn>
            <span>{{ currentIndex + 1 }} / {{ instanceCount }}</span>
            <v-btn
              icon
              small
              :disabled="currentIndex >= instanceCount - 1"
              @click="nextImage"
            >
              <v-icon>chevron_right</v-icon>
            </v-btn>
          </div>
        </div>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script>
// dwv：原生 DICOM viewer（MIT），瀏覽器端用真實 DOM canvas 渲染原生 .dcm。
// 後端不轉檔，原生 DICOM 由 dwv 解碼，保留 window/level 等診斷級互動（決策 2）。
// ⚠️ 必須用 namespace import：dwv 0.34.2 是 UMD 套件、**只具名匯出**（App / AppOptions /
//    ViewConfig，見 dwv.d.ts），**沒有 default export**。`import dwv from 'dwv'` 會讓 dwv=undefined
//    → `new dwv.App()` 拋 `Cannot read properties of undefined (reading 'App')`、影像整片黑屏。
//    build 照樣綠（webpack 不檢查 CJS default 是否存在），只有真瀏覽器開才炸——故改 import 後
//    務必用真瀏覽器複驗到影像真的畫出來（本檔用到 dwv.App / dwv.ViewConfig / dwv.AppOptions 三者，
//    皆為具名匯出，namespace import 全取得到）。
import * as dwv from 'dwv'
import pacsImage from '@/services/pacsImage'

export default {
  name: 'PacsDicomViewer',

  props: {
    show: { type: Boolean, default: false },
    // 當前病人的 PatientID（病歷號）與 accessionNumber。
    // ⚠️ 兩者都由 ExaminationDetailPage 從 EVAS 自己的 DB 帶入
    // （accessionNumber = examinations.accNo，由 worklist sync 寫入），
    // **不是使用者手動輸入**——醫位沒有「用病人 ID 列歷史檢查」的 API，
    // 檢查單號是我方唯一能拿來查醫位影像的鍵。
    patientId: { type: String, default: '' },
    accessionNumber: { type: String, default: '' },
  },

  data() {
    return {
      loadingImageSets: false,
      retrieving: false,
      errorMsg: '',
      imageSets: [],
      activeImageSetId: '',
      instanceUrls: [],
      failedCount: 0, // 本次取像失敗張數（部分失敗時 > 0）
      currentIndex: 0,
      dwvApp: null, // dwv.App 實例，destroy 時需手動釋放避免記憶體洩漏
    }
  },

  computed: {
    instanceCount() {
      return this.instanceUrls.length
    },
  },

  watch: {
    // dialog 開啟時才查（避免無謂的 PACS 查詢）
    show(val) {
      if (val) {
        this.queryImageSets()
      } else {
        this.teardownDwv()
      }
    },
  },

  beforeDestroy() {
    // 元件銷毀務必釋放 dwv，否則 canvas / event listener 殘留造成洩漏
    this.teardownDwv()
  },

  methods: {
    close() {
      this.$emit('close')
    },

    // --- 清單顯示：ImageSet 的 metadata 全是選填，一律 fallback ----------
    imageSetTitle(imageSet) {
      // 有日期就顯示日期（DIMSE），沒有就退回檢查單號（醫位），
      // 再沒有才退回 imageSetId——永遠給得出一個可辨識的標題
      if (imageSet.studyDate) return this.formatDate(imageSet.studyDate)
      if (imageSet.accessionNumber) {
        return `${this.$t('pacs.accession')} ${imageSet.accessionNumber}`
      }
      return imageSet.imageSetId
    },

    imageSetSubtitle(imageSet) {
      const parts = []
      if (imageSet.modality) parts.push(imageSet.modality)
      if (imageSet.description) parts.push(imageSet.description)
      const count = imageSet.numberOfInstances
      if (count != null && count !== '') {
        parts.push(this.$t('pacs.image_count', { count }))
      }
      return parts.length ? parts.join(' · ') : this.$t('pacs.no_desc')
    },

    async queryImageSets() {
      this.errorMsg = ''
      this.imageSets = []
      this.activeImageSetId = ''
      this.instanceUrls = []
      this.failedCount = 0
      if (!this.patientId && !this.accessionNumber) {
        this.errorMsg = this.$t('pacs.no_patient_context')
        return
      }
      this.loadingImageSets = true
      try {
        this.imageSets = await pacsImage.findImageSets({
          patientId: this.patientId,
          accessionNumber: this.accessionNumber,
        })
      } catch (e) {
        this.errorMsg = e.message
      } finally {
        this.loadingImageSets = false
      }
    },

    async onSelectImageSet(imageSet) {
      if (this.retrieving) return
      this.activeImageSetId = imageSet.imageSetId
      this.errorMsg = ''
      this.failedCount = 0
      this.retrieving = true
      try {
        // 觸發後端取像（DIMSE C-MOVE/C-GET 或醫位 WADO 的 N 次 HTTP GET）
        const { instances, failedCount } = await pacsImage.retrieveImageSet(
          imageSet.imageSetId
        )
        this.failedCount = failedCount || 0
        if (!instances || instances.length === 0) {
          this.errorMsg = this.$t('pacs.no_image_retrieved')
          this.instanceUrls = []
          return
        }
        this.instanceUrls = instances.map((file) =>
          pacsImage.instanceUrl(imageSet.imageSetId, file)
        )
        this.currentIndex = 0
        await this.loadCurrentImage()
      } catch (e) {
        this.errorMsg = e.message
      } finally {
        this.retrieving = false
      }
    },

    // --- dwv 生命週期 ----------------------------------------------------
    ensureDwvApp() {
      if (this.dwvApp) return this.dwvApp
      const app = new dwv.App()
      const viewConfig = new dwv.ViewConfig('pacs-dwv-layer-group')
      const viewConfigs = { '*': [viewConfig] }
      const options = new dwv.AppOptions(viewConfigs)
      // 內建工具：window/level（拖曳調明暗）+ ZoomAndPan（縮放平移）
      options.tools = {
        WindowLevel: {},
        ZoomAndPan: {},
      }
      app.init(options)
      app.addEventListener('load', () => {
        // 載入完成預設啟用 window/level 工具
        try {
          app.setTool('WindowLevel')
        } catch (e) {
          // 某些 modality 不支援時忽略
        }
      })
      app.addEventListener('error', (event) => {
        this.errorMsg = this.$t('pacs.decode_error')
        // eslint-disable-next-line no-console
        void 0
      })
      this.dwvApp = app
      return app
    },

    async loadCurrentImage() {
      const url = this.instanceUrls[this.currentIndex]
      if (!url) return
      const app = this.ensureDwvApp()
      // 先清掉上一張的資料再載入新影像（避免多 study 影像疊加）
      try {
        app.reset()
      } catch (e) {
        // 第一次載入時尚無資料，reset 可能 noop
      }
      app.loadURLs([url])
    },

    nextImage() {
      if (this.currentIndex < this.instanceCount - 1) {
        this.currentIndex += 1
        this.loadCurrentImage()
      }
    },

    prevImage() {
      if (this.currentIndex > 0) {
        this.currentIndex -= 1
        this.loadCurrentImage()
      }
    },

    teardownDwv() {
      if (this.dwvApp) {
        try {
          this.dwvApp.reset()
        } catch (e) {
          // ignore
        }
        this.dwvApp = null
      }
      this.instanceUrls = []
      this.currentIndex = 0
    },

    // DICOM StudyDate（YYYYMMDD）→ YYYY/MM/DD
    formatDate(d) {
      if (!d || d.length < 8) return d || '-'
      return `${d.slice(0, 4)}/${d.slice(4, 6)}/${d.slice(6, 8)}`
    },
  },
}
</script>

<style lang="scss" scoped>
.pacs-viewer__title {
  display: flex;
  align-items: center;
  font-size: 18px;
}
.pacs-viewer__body {
  display: flex;
  height: 600px;
  padding: 0;
}
.pacs-viewer__sidebar {
  width: 280px;
  border-right: 1px solid #e0e0e0;
  overflow-y: auto;
  padding: 8px;
}
.pacs-viewer__section-label {
  font-weight: bold;
  margin-bottom: 8px;
}
.pacs-viewer__hint {
  color: #888;
  font-size: 13px;
  padding: 12px 4px;
}
.pacs-viewer__study--active {
  background: rgba(33, 150, 243, 0.12);
}
.pacs-viewer__main {
  flex: 1;
  position: relative;
  background: #000;
}
.pacs-viewer__canvas {
  width: 100%;
  height: 100%;
}
.pacs-viewer__overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  color: #fff;
  background: rgba(0, 0, 0, 0.55);
  z-index: 2;
}
.pacs-viewer__error {
  position: absolute;
  top: 12px;
  left: 12px;
  right: 12px;
  color: #ff5252;
  background: rgba(0, 0, 0, 0.6);
  padding: 8px 12px;
  border-radius: 4px;
  z-index: 3;
  font-size: 13px;
}
/* 部分失敗提示：用警告色（琥珀）與錯誤（紅）區隔——影像有出來、只是不齊全 */
.pacs-viewer__warning {
  position: absolute;
  top: 12px;
  left: 12px;
  right: 12px;
  color: #ffb300;
  background: rgba(0, 0, 0, 0.6);
  padding: 8px 12px;
  border-radius: 4px;
  z-index: 3;
  font-size: 13px;
}
.pacs-viewer__pager {
  position: absolute;
  bottom: 12px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  align-items: center;
  gap: 8px;
  color: #fff;
  background: rgba(0, 0, 0, 0.5);
  padding: 4px 12px;
  border-radius: 20px;
  z-index: 2;
}
</style>
